import { CheckCircle2 } from "lucide-react";
import { useAppSettings } from "@/hooks/useAppSettings";
import { DISPLAY_PHONE, toTelHref } from "@/lib/phone";
import { readPlanHandoff } from "@/lib/plan-handoff";
import { menuPhrase } from "@/lib/plan-menus";

/**
 * Shown at the top of /menu/<slug>?from=plan, where the plan wizard lands a
 * visitor after they submit (#1221). The first name comes from the wizard's
 * sessionStorage hand-off; without it the banner just says "Thanks!". Plain
 * text, not a heading, so the menu title stays the page's h1.
 */
export function PlanThanksBanner({ planRef, menuTitle }: { planRef: string | null; menuTitle: string | null }) {
  const firstName = readPlanHandoff(planRef);
  const thanks = firstName ? `Thanks, ${firstName}.` : "Thanks!";
  const message = menuTitle
    ? `Here's our ${menuPhrase(menuTitle)}. We've also sent it to your email and phone.`
    : "We got your request and we'll be in touch within 24 hours.";
  return (
    <div className="bg-primary/10 border-b border-primary/20">
      <div role="status" className="max-w-4xl mx-auto px-4 py-4 flex items-start gap-3">
        <CheckCircle2 className="h-5 w-5 shrink-0 text-primary mt-0.5" aria-hidden="true" />
        <p className="text-sm md:text-base text-foreground">
          <span className="font-semibold">{thanks}</span> {message}
        </p>
      </div>
    </div>
  );
}

/** "Questions? Call <phone>": replaces the menu page's inquiry form for
 * visitors who already submitted the plan wizard (#1221). */
export function PlanCallLine() {
  const { data: settings } = useAppSettings();
  const phone = settings?.business_phone || DISPLAY_PHONE;
  const telHref = toTelHref(phone) === "tel:" ? toTelHref(DISPLAY_PHONE) : toTelHref(phone);
  return (
    <p className="text-center text-base text-foreground py-8">
      Questions? Call{" "}
      <a href={telHref} className="font-semibold text-primary underline underline-offset-2 hover:no-underline">
        {phone}
      </a>
    </p>
  );
}
